/**
 * Generic Utils - Handlebars Helpers
 */

// Internal Helpers
const isArray = (thing: unknown[]): boolean => Object.prototype.toString.call(thing) === '[object Array]'
const nullSafeString = (value: string): string => value ?? ''

/**
 * Handlebars adds a final argument to the function call. This helpers strip out the last argument
 * if it appears to be a handlebars options object.
 */
const stripHandlebarsOptions = (name: string, values: unknown[]) => {
  const lastValue = values[values.length - 1]
  if (lastValue && typeof lastValue === 'object' && 'name' in lastValue && lastValue.name === name) {
    return values.slice(0, values.length - 1)
  }
  return values
}

// Wrapper to use all arguments passed through stripHandlebarsOptions
const withValues =
  <T>(name: string, fn: (values: unknown[]) => T) =>
  (...args: unknown[]) =>
    fn(stripHandlebarsOptions(name, args))

// Logical comparisons
export const eq = (value1: unknown, value2: unknown): boolean => value1 === value2

export const uneq = (value1: unknown, value2: unknown): boolean => value1 !== value2

export const lt = (value1: any, value2: any): boolean => value1 < value2

export const lte = (value1: any, value2: any): boolean => value1 <= value2

export const gt = (value1: any, value2: any): boolean => value1 > value2

export const gte = (value1: any, value2: any): boolean => value1 >= value2

export const and = withValues('and', (values: unknown[]): boolean => values.every(Boolean))

export const or = withValues('or', (values: unknown[]): boolean => values.some(Boolean))

// Array utils
export const length = (array: unknown[]): string => {
  if (!isArray(array)) {
    return ''
  }

  return array.length.toString()
}

export const includes = (array: unknown[], value: unknown): boolean => {
  if (!isArray(array) || array.length === 0) {
    return false
  }

  for (const item of array) {
    if (item === value) {
      return true
    }
  }

  return false
}

// String utils
// These functions need to check for undefined/null values to avoid errors
// when called only during runtime (eg. as a Handlebars helper)
export const lowercase = (value: string): string => nullSafeString(value).toLowerCase()

export const uppercase = (value: string): string => nullSafeString(value).toUpperCase()

export const contains = (string: string, substring: string): boolean => nullSafeString(string).includes(substring)

export const stringify = (value: unknown): string => JSON.stringify(value)

export const concat = withValues('concat', (values: unknown[]): string => {
  if (values.length === 0) {
    return ''
  }

  const separator = String(values[0])
  const strings = values
    .slice(1)
    .filter((value) => value != null)
    .map(String)
  return strings.join(separator)
})

export const substring = (value: string, direction: string, count: unknown): string => {
  const safeValue = nullSafeString(value)
  const numCount = Number(count)

  if (!safeValue || !direction || isNaN(numCount) || numCount < 0) {
    return safeValue
  }

  if (direction === 'left') {
    return safeValue.substring(0, numCount)
  } else if (direction === 'right') {
    return safeValue.substring(Math.max(0, safeValue.length - numCount))
  }

  return safeValue
}

export const truncate = (value: string, maxLength: unknown, suffix?: string): string => {
  const safeValue = nullSafeString(value)
  const numMaxLength = Number(maxLength)
  const safeSuffix = suffix ?? '…'

  if (!safeValue || isNaN(numMaxLength) || numMaxLength < 0) {
    return safeValue
  }

  if (safeValue.length <= numMaxLength) {
    return safeValue
  }

  return safeValue.substring(0, Math.max(0, numMaxLength)) + safeSuffix
}

// Math utils
export const add = withValues('add', (values: unknown[]): number =>
  values.reduce((acc: number, value) => acc + Number(value), 0),
)

export const subtract = withValues('subtract', (values: unknown[]): number =>
  values.length === 0 ? 0 : Number(values[0]) - add(...values.slice(1)),
)

export const multiply = withValues('multiply', (values: unknown[]): number =>
  values.reduce((acc: number, value) => acc * Number(value), 1),
)

export const divide = withValues('divide', (values: unknown[]): number => {
  if (values.length === 0 || Number(values[0]) === 0) {
    return 0
  }

  const denominator = multiply(...values.slice(1))
  return denominator === 0 ? 0 : Number(values[0]) / denominator
})

export const round = (value: unknown, precision?: unknown) => Number(value).toFixed(Number(precision) || 0)

export const modulo = (value: unknown, divisor: unknown): number => Number(value) % Number(divisor)

export const percent = (value: unknown, total: unknown): number => (Number(value) / Number(total)) * 100
